import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { SweepingPropertiesEdit } from "./SweepingPropertiesEdit";
import { Formik, FormikProps } from "formik";
import { SweepingFormValues } from "./SweepingFormValues";
import { FormikErrors, FormikHelpers } from "formik/dist/types";
import { _throw, IllegalStateError, notNull } from "shared-ts-utils-core";
import {
  FlueId,
  quantityDto,
  Sweeping,
  SweepingImpl,
  useSweepingDao,
} from "firefly-shared-ts-domain";
import { PersistedState } from "./PersistedState";
import { DateTimeUtils } from "shared-ts-utils-luxon";
import { ValidityReporter } from "shared-ts-formik";
import { useTranslation } from "react-i18next";
import { Alert } from "@mui/material";

const defaultFormValues: SweepingFormValues = {
  time: null,
  sootingDegree: null,
  dueness: null,
  wasteAmount: null,
  wasteAmountValue: null,
  wasteAmountUnit: "kg",
  wasteComposition: null,
  creosoteAmountValue: null,
  creosoteAmountUnit: "%",
  sootAmountValue: null,
  sootAmountUnit: "%",
  ashAmountValue: null,
  ashAmountUnit: "%",
};

export interface SweepingPropertiesFormProps {
  flueId: FlueId;
  sweeping: Sweeping | undefined;
  persistedState: PersistedState;
  onPersisted?: (value: Sweeping) => void;
  onPersistedStateChange?: (
    sweeping: Sweeping | undefined,
    value: PersistedState
  ) => void;
  onValidityChange?: (isValid: boolean) => void;
}

export interface SweepingPropertiesFormHandle {
  submitForm: () => void;
  resetForm: () => void;
}

export const SweepingPropertiesForm = forwardRef<
  SweepingPropertiesFormHandle,
  SweepingPropertiesFormProps
>((props, ref) => {
  const onPersistedStateChange = props.onPersistedStateChange;

  const { t } = useTranslation("firefly-chimney-insights-ts-domain");
  const [errorMessages, setErrorMessages] = React.useState<string[]>([]);

  const sweeping = props.sweeping;

  const initialValues: SweepingFormValues = useMemo(() => {
    if (sweeping == null) {
      return defaultFormValues;
    }

    const initialValues: SweepingFormValues = {
      time: sweeping.time.toISO(),
      sootingDegree: sweeping.sootingDegree,
      dueness: sweeping.dueness,
      wasteAmount: sweeping.wasteAmount?.value != null,
      wasteAmountValue: sweeping.wasteAmount?.value ?? null,
      wasteAmountUnit: sweeping.wasteAmount?.unit ?? "kg",
      wasteComposition: sweeping.hasWasteComposition(),
      creosoteAmountValue: sweeping.creosoteAmount?.value ?? null,
      creosoteAmountUnit:
        sweeping.creosoteAmount?.unit ?? defaultFormValues.creosoteAmountUnit,
      sootAmountValue: sweeping.sootAmount?.value ?? null,
      sootAmountUnit:
        sweeping.sootAmount?.unit ?? defaultFormValues.sootAmountUnit,
      ashAmountValue: sweeping.ashAmount?.value ?? null,
      ashAmountUnit:
        sweeping.ashAmount?.unit ?? defaultFormValues.ashAmountUnit,
    };

    return initialValues;
  }, [sweeping]);

  const sweepingDao = useSweepingDao();

  const validateForm = (values: SweepingFormValues) => {
    const errors: FormikErrors<SweepingFormValues> = {};
    if (values.time == null) {
      errors.time = t("SweepingFormValidation:time!=0");
    }

    if (values.sootingDegree == null) {
      errors.sootingDegree = t("SweepingFormValidation:sootingDegree!=0");
    }

    if (values.wasteAmount == null) {
      errors.wasteAmount = t("SweepingFormValidation:wasteAmount!=0");
    }

    if (values.wasteAmount === true && values.wasteAmountValue == null) {
      errors.wasteAmountValue = t("SweepingFormValidation:wasteAmountValue!=0");
    }

    if (values.wasteAmountValue != null && values.wasteAmountValue < 0) {
      errors.wasteAmountValue = t("SweepingFormValidation:wasteAmountValue>=0");
    }

    if (
      (values.sootingDegree ?? 0) > 0 &&
      values.wasteAmount === true &&
      values.wasteAmountValue === 0
    ) {
      errors.wasteAmountValue = t(
        t("SweepingFormValidation:wasteAmountValue=sootingDegree")
      );
    }

    if (values.wasteAmountValue != null && values.wasteComposition == null) {
      errors.wasteComposition = t("SweepingFormValidation:wasteComposition!=0");
    }

    if (values.wasteComposition === true) {
      if (values.creosoteAmountValue == null) {
        errors.creosoteAmountValue = t(
          "SweepingFormValidation:creosoteAmountValue!=0"
        );
      }
      if (values.sootAmountValue == null) {
        errors.sootAmountValue = t("SweepingFormValidation:sootAmountValue!=0");
      }
      if (values.ashAmountValue == null) {
        errors.ashAmountValue = t("SweepingFormValidation:ashAmountValue!=0");
      }
    }

    return errors;
  };

  const handleSubmit = async (
    values: SweepingFormValues,
    formikHelpers: FormikHelpers<SweepingFormValues>
  ) => {
    const errors = validateForm(values);
    setErrorMessages(Object.values(errors));

    if (
      values.dueness == null ||
      values.sootingDegree == null ||
      values.time == null
    ) {
      return;
    }

    const time = DateTimeUtils.fromISO(values.time);

    if (props.persistedState === "new") {
      const created = new SweepingImpl(
        await sweepingDao.create({
          flueId: props.flueId,
          time: time.toUTC().toISO(),
          sootingDegree: values.sootingDegree,
          dueness: values.dueness,
          wasteAmount:
            values.wasteAmountValue != null
              ? quantityDto(values.wasteAmountValue, values.wasteAmountUnit)
              : undefined,
          creosoteAmount:
            notNull(values.creosoteAmountValue, (it) =>
              quantityDto(it, values.creosoteAmountUnit)
            ) ?? undefined,
          sootAmount:
            notNull(values.sootAmountValue, (it) =>
              quantityDto(it, values.sootAmountUnit)
            ) ?? undefined,
        })
      );
      props.onPersisted?.(created);
    } else if (props.persistedState === "dirty") {
      const updated = new SweepingImpl(
        await sweepingDao.update({
          id:
            props.sweeping?.id ??
            _throw(new IllegalStateError("id should not be null")),
          time: time.toUTC().toISO(),
          sootingDegree: values.sootingDegree,
          dueness: values.dueness,
          wasteAmount:
            values.wasteAmountValue != null
              ? quantityDto(values.wasteAmountValue, values.wasteAmountUnit)
              : undefined,
          creosoteAmount:
            notNull(values.creosoteAmountValue, (it) =>
              quantityDto(it, values.creosoteAmountUnit)
            ) ?? undefined,
          sootAmount:
            notNull(values.sootAmountValue, (it) =>
              quantityDto(it, values.sootAmountUnit)
            ) ?? undefined,
        })
      );
      props.onPersisted?.(updated);
    }
  };
  const formikRef = useRef<FormikProps<SweepingFormValues>>(null);
  useImperativeHandle(ref, () => ({
    submitForm: () => {
      formikRef.current?.submitForm();
    },
    resetForm: () => {
      formikRef.current?.resetForm();
    },
  }));

  const handleDirtyChange = (dirty: boolean) => {
    if (props.persistedState !== "dirty" && dirty) {
      onPersistedStateChange?.(props.sweeping, "dirty");
    } else if (props.persistedState === "dirty" && !dirty) {
      onPersistedStateChange?.(props.sweeping, "persisted");
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validateOnMount
      onSubmit={handleSubmit}
      validate={validateForm}
    >
      {({
        values,
        errors,
        touched,
        dirty,
        isValid,
        setFieldValue,
        setValues,
        setFieldTouched,
      }) => {
        return (
          <>
            <ValidityReporter
              isValid={isValid}
              onValidityChange={props.onValidityChange}
            />
            <SweepingPropertiesEdit
              persistedState={props.persistedState}
              values={values}
              touched={touched}
              errors={errors}
              dirty={dirty}
              setFieldValue={setFieldValue}
              setValues={setValues}
              setFieldTouched={setFieldTouched}
              onDirtyChange={handleDirtyChange}
            />
            {errorMessages.length > 0 &&
              errorMessages.map((message) => (
                <Alert severity={"error"}>{message}</Alert>
              ))}
          </>
        );
      }}
    </Formik>
  );
});
